import * as React from "react";
import {
    Alert,
    AlertColor,
    Autocomplete,
    Box,
    Divider,
    Stack,
    TextField,
    TextFieldProps,
    Typography,
} from "@mui/material";
import CclDrawer from "../../../../components/common/cclDrawer/cclDrawer";
import CclDrawerActionBar from "../../../../components/common/cclDrawer/cclDrawerActionBar";
import CclLoadingButton from "../../../../components/common/cclButtons/cclLoadingButton";
import CclButton from "../../../../components/common/cclButtons/cclButton";
import { DatePicker } from "@mui/x-date-pickers";
import {
    useGetPartnerSessionListQuery,
    useGetUserSessionListQuery,
} from "../../../../services/cclTokenedSessionApi";
import {
    Participant,
    TransferParticipantRequest,
    TransferParticipantResponse,
} from "../../../../services/types/enterpriseParticipantApiTypes";
import {
    AvailableTransferSession,
    SessionDetailType,
} from "../../../../services/types/sessionDetailTypes";
import { AccessEventRegistrationDetails } from "../../../../services/types/accessEventTypes";
import {
    useTransferPartnerSessionParticipantsMutation,
    useTransferProductSaleParticipantsMutation,
} from "../../../../services/cclTokenedEnterpriseParticipantApi";
import useLogAccessEvent from "../../../../hooks/useLogAccessEvent";
import { getUtcDateStringWithoutOffset, parseDateAsLocal } from "../../../../utilities/dateHelpers";

interface TransferParticipantDrawerProps {
    open: boolean;
    participant: Participant;
    fromSession: SessionDetailType;
    onClose: () => void;
    showSnackbar: (message: string, severity: AlertColor) => void;
}

const TransferParticipantDrawer: React.FC<TransferParticipantDrawerProps> = (props) => {
    const [transferInProgress, setTransferInProgress] = React.useState(false);
    const [sessionList, setSessionList] = React.useState<AvailableTransferSession[]>([]);
    const [selectedSession, setSelectedSession] = React.useState<AvailableTransferSession | null>(
        null,
    );
    const [completionDate, setCompletionDate] = React.useState<Date | null>(null);

    const isLicensee = props.fromSession.managingLocationKey === 6;

    const { data: productSaleSessionList } = useGetUserSessionListQuery(
        props.participant.esKey.toString(),
        { skip: isLicensee },
    );
    const { data: partnerSessionList } = useGetPartnerSessionListQuery(
        props.participant.esKey.toString(),
        { skip: !isLicensee },
    );
    const userSessionList = isLicensee ? partnerSessionList : productSaleSessionList;
    const [transferProductSaleParticipant] = useTransferProductSaleParticipantsMutation();
    const [transferPartnerSessionParticipant] = useTransferPartnerSessionParticipantsMutation();
    const { logEvent } = useLogAccessEvent();

    React.useEffect(() => {
        const sessList =
            userSessionList
                ?.slice()
                .sort((a: AvailableTransferSession, b: AvailableTransferSession) =>
                    a.sessionTitle.localeCompare(b.sessionTitle),
                ) || [];
        setSessionList(sessList);
    }, [userSessionList]);

    // Sync completion date with selected session's survey completion date
    React.useEffect(() => {
        if (!selectedSession) {
            setCompletionDate(null);
            return;
        }

        const rawSurveyCompletionDate = selectedSession.surveyCompletionDate;

        if (!rawSurveyCompletionDate) {
            setCompletionDate(null);
            return;
        }

        // Ensure the survey completion date is parsed as UTC to avoid timezone-dependent day shifts.
        // If the string already includes a timezone ("Z" or an explicit offset), use it as-is.
        const hasTimezone =
            /Z$/i.test(rawSurveyCompletionDate) ||
            /[+-]\d{2}:?\d{2}$/.test(rawSurveyCompletionDate);
        const normalizedRawSurveyCompletionDate = hasTimezone
            ? rawSurveyCompletionDate
            : `${rawSurveyCompletionDate}Z`;
        const parsed = new Date(normalizedRawSurveyCompletionDate);

        if (isNaN(parsed.getTime())) {
            // Fallback to null if the date cannot be parsed
            setCompletionDate(null);
            return;
        }

        // Normalize to local midnight for the UTC calendar day to avoid timezone day-shift
        const normalized = parseDateAsLocal(parsed);

        setCompletionDate(normalized);
    }, [selectedSession]);

    // Compute validation messages synchronously
    const validationMessages = React.useMemo((): string[] => {
        const messages: string[] = [];
        let endDate: Date | null = null;
        const rawEnd = selectedSession?.end;
        if (rawEnd) {
            // Parse end date from its declared string representation
            const parsedEnd = new Date(rawEnd);
            if (!isNaN(parsedEnd.getTime())) {
                // Normalize to local date to avoid timezone day-shift in comparisons
                endDate = parseDateAsLocal(parsedEnd);
            }
        }

        // Assessment Completion: Must be before Session End Date
        if (completionDate && endDate && completionDate >= endDate) {
            messages.push("Assessment Completion must be before Session End Date");
        }

        return messages;
    }, [completionDate, selectedSession]);

    const handleTransferCompleted = (response: TransferParticipantResponse) => {
        setTransferInProgress(false);
        if (response.results.length > 0 && response.results[0].success) {
            props.showSnackbar("Participant Transferred", "success");
            const eventData: AccessEventRegistrationDetails = {
                esiKey: props.participant.esiKey.toString(),
                imKey: props.participant.imKey.toString(),
                email: props.participant.emailAddress,
            };
            logEvent("ParticipantTransferred", eventData);
            props.onClose();
        } else {
            props.showSnackbar("Transfer Participant Failed", "error");
        }
    };

    const handleSave = async () => {
        if (selectedSession == null) return;

        setTransferInProgress(true);
        const payload: TransferParticipantRequest = {
            sourceEsKey: props.participant.esKey,
            destEsKey: selectedSession.esKey,
            esiKeys: [props.participant.esiKey],
            assessmentCompletionDate: completionDate
                ? getUtcDateStringWithoutOffset(completionDate)
                : undefined,
        };
        if (isLicensee) {
            await transferPartnerSessionParticipant(payload)
                .unwrap()
                .then((response) => {
                    handleTransferCompleted(response);
                })
                .catch(() => {
                    setTransferInProgress(false);
                    props.showSnackbar("Transfer Participant Failed", "error");
                });
        } else {
            await transferProductSaleParticipant(payload)
                .unwrap()
                .then((response) => {
                    handleTransferCompleted(response);
                })
                .catch(() => {
                    setTransferInProgress(false);
                    props.showSnackbar("Transfer Participant Failed", "error");
                });
        }
    };

    const getSessionLabel = (option: AvailableTransferSession): string => {
        const startDate = option.start ? new Date(option.start).toLocaleDateString() : "";
        if (isLicensee) {
            return startDate
                ? `${option.sessionTitle} (${option.sessionId}, ${startDate})`
                : option.sessionTitle;
        }
        return startDate ? `${option.sessionTitle} (${startDate})` : option.sessionTitle;
    };

    return (
        <CclDrawer
            title={"Transfer Participant"}
            open={props.open}
            onClose={props.onClose}
            width="standard"
        >
            <Box sx={{ overflow: "hidden", height: 1 }}>
                <Stack spacing={2} sx={{ overflow: "hidden", pt: 2 }}>
                    <TextField
                        fullWidth
                        label="Participant"
                        value={`${props.participant.firstName} ${props.participant.lastName}`}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{
                            "& .MuiInputBase-input": {
                                paddingTop: "8px !important",
                                paddingBottom: "8px !important",
                            },
                            mb: 1,
                        }}
                    />

                    <TextField
                        fullWidth
                        label="From Session"
                        value={`${props.fromSession.title}${
                            props.fromSession?.start
                                ? ` (${new Date(props.fromSession.start).toLocaleDateString()})`
                                : ""
                        }`}
                        InputProps={{
                            readOnly: true,
                        }}
                        sx={{
                            "& .MuiInputBase-input": {
                                paddingTop: "8px !important",
                                paddingBottom: "8px !important",
                            },
                        }}
                    />

                    <Divider sx={{ marginTop: "16px !important", mb: 1 }} />

                    <Typography
                        variant="h6"
                        sx={{ mt: 0, mb: 2, fontWeight: 500, fontSize: "1rem" }}
                    >
                        Destination
                    </Typography>

                    <Autocomplete
                        fullWidth
                        size="small"
                        options={sessionList}
                        getOptionLabel={getSessionLabel}
                        onChange={(event, newValue) => setSelectedSession(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Transfer To Session"
                                placeholder="Transfer To Session"
                            />
                        )}
                    />

                    <DatePicker
                        label={"Assessment Completion"}
                        value={completionDate}
                        disabled={!selectedSession}
                        minDate={selectedSession ? new Date() : undefined}
                        maxDate={
                            selectedSession?.end
                                ? (() => {
                                      const maxDate = new Date(selectedSession.end);
                                      maxDate.setDate(maxDate.getDate() - 1);
                                      return maxDate;
                                  })()
                                : undefined
                        }
                        renderInput={(params: JSX.IntrinsicAttributes & TextFieldProps) => {
                            params.fullWidth = true;
                            return (
                                <TextField
                                    {...params}
                                    error={false}
                                    onKeyDown={(e) => {}}
                                    size="small"
                                />
                            );
                        }}
                        onChange={(e) => setCompletionDate(e)}
                    />

                    {validationMessages.length > 0 &&
                        validationMessages.map((message, index) => (
                            <Alert key={index} severity="info">
                                {message}
                            </Alert>
                        ))}
                </Stack>
                <CclDrawerActionBar>
                    <CclLoadingButton
                        mode={"primary"}
                        disabled={!selectedSession || validationMessages.length > 0}
                        onClick={handleSave}
                        loading={transferInProgress}
                    >
                        Transfer
                    </CclLoadingButton>
                    <CclButton
                        sx={{ float: "right", mr: 4 }}
                        mode={"secondary"}
                        onClick={props.onClose}
                    >
                        Cancel
                    </CclButton>
                </CclDrawerActionBar>
            </Box>
        </CclDrawer>
    );
};

export default TransferParticipantDrawer;
